/* eslint-disable no-console */
import * as Sentry from "@sentry/vue";

const LOG_LEVELS: Record<string, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
  silent: 4,
} as const;

type LogLevel = keyof typeof LOG_LEVELS;
type LogContext = Record<string, unknown> | Error;
type LogFn = (message: string, context?: LogContext) => void;

const isValidLogLevel = (level: string): level is LogLevel => {
  return level in LOG_LEVELS;
};

const ENV_LOG_LEVEL: LogLevel = isValidLogLevel(import.meta.env.VITE_LOG_LEVEL)
  ? import.meta.env.VITE_LOG_LEVEL
  : "warn";

const shouldLog = (level: LogLevel): boolean => {
  return LOG_LEVELS[level] >= LOG_LEVELS[ENV_LOG_LEVEL];
};

const log = (level: LogLevel, message: string, context?: LogContext): void => {
  if (!shouldLog(level)) {
    return;
  }

  if (import.meta.env.DEV || import.meta.env.MODE === "staging") {
    const logFn = console[level] ?? console.log;
    logFn(`[${level.toUpperCase()}]`, message, context);
  }

  if (level === "error" && import.meta.env.MODE === "production") {
    const extra =
      context instanceof Error
        ? { name: context.name, message: context.message, stack: context.stack }
        : context;

    const event: Sentry.Event = {
      level,
      message,
      extra,
    };

    Sentry.captureEvent(event);
  }
};

const logger: Record<LogLevel, LogFn> = {
  debug: (msg, ctx): void => log("debug", msg, ctx),
  info: (msg, ctx): void => log("info", msg, ctx),
  warn: (msg, ctx): void => log("warn", msg, ctx),
  error: (msg, ctx): void => log("error", msg, ctx),
  // No-op
  silent: () => {},
};

export default logger;
